<template>
  <section id="home" class="hero">
    <!-- Animated background -->
    <div class="hero__bg" aria-hidden="true">
      <div class="hero__grid"></div>
      <div class="hero__orb hero__orb--1"></div>
      <div class="hero__orb hero__orb--2"></div>
      <div class="hero__orb hero__orb--3"></div>
      <div class="hero__beam"></div>
    </div>

    <div class="ec-container hero__inner">
      <div class="hero__copy">
        <span class="hero__badge" v-reveal>
          <span class="dot"></span> {{ $t('hero.badge') }}
        </span>

        <h1 class="hero__title" v-reveal="80">
          {{ $t('hero.titlePre1') }}<span class="ec-gradient-text">{{ $t('hero.titleHi1') }}</span><br />
          {{ $t('hero.titlePre2') }}<span class="ec-gradient-text">{{ $t('hero.titleHi2') }}</span>
        </h1>

        <p class="hero__lead" v-reveal="160">
          {{ $t('hero.lead') }}
        </p>

        <div class="hero__actions" v-reveal="240">
          <a href="#contact" class="ec-btn ec-btn-primary">{{ $t('hero.ctaPrimary') }}</a>
          <a href="#products" class="ec-btn ec-btn-ghost">{{ $t('hero.ctaGhost') }}</a>
        </div>

        <div class="hero__stats" v-reveal="320">
          <div v-for="s in stats" :key="s.label" class="hero__stat">
            <strong>{{ s.value }}</strong>
            <span>{{ s.label }}</span>
          </div>
        </div>
      </div>

      <div class="hero__visual" v-reveal="200">
        <div class="panel">
          <div class="panel__top">
            <span class="panel__dots"><i></i><i></i><i></i></span>
            <span class="panel__title">{{ $t('hero.panelTitle') }}</span>
            <span class="panel__live"><i></i> {{ $t('hero.panelLive') }}</span>
          </div>
          <div class="panel__body">
            <div class="metric">
              <div class="metric__row">
                <span>{{ $t('hero.utilLabel') }}</span><strong class="ec-gradient-text">98.6%</strong>
              </div>
              <div class="bar"><span style="width: 86%"></span></div>
            </div>
            <div class="metric__grid">
              <div v-for="m in mini" :key="m.k" class="mini">
                <span class="mini__k">{{ m.k }}</span>
                <strong class="mini__v">{{ m.v }}</strong>
                <span class="mini__d" :class="m.up === false ? 'down' : 'up'">{{ m.d }}</span>
              </div>
            </div>
            <div class="spark">
              <svg viewBox="0 0 280 70" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="sparkFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stop-color="#FF9900" stop-opacity="0.45" />
                    <stop offset="1" stop-color="#FF9900" stop-opacity="0" />
                  </linearGradient>
                </defs>
                <path d="M0 52 L28 46 L56 50 L84 34 L112 40 L140 24 L168 30 L196 16 L224 22 L252 9 L280 14 V70 H0 Z" fill="url(#sparkFill)" />
                <path d="M0 52 L28 46 L56 50 L84 34 L112 40 L140 24 L168 30 L196 16 L224 22 L252 9 L280 14" fill="none" stroke="#FFB84D" stroke-width="2.4" stroke-linejoin="round" stroke-linecap="round" />
              </svg>
            </div>
          </div>
        </div>
        <div class="float-card float-card--a">
          <span class="float-card__icon">🛡️</span>
          <div><strong>{{ $t('hero.floatA.title') }}</strong><em>{{ $t('hero.floatA.sub') }}</em></div>
        </div>
        <div class="float-card float-card--b">
          <span class="float-card__icon">⚡</span>
          <div><strong>{{ $t('hero.floatB.title') }}</strong><em>{{ $t('hero.floatB.sub') }}</em></div>
        </div>
      </div>
    </div>

    <div class="hero__marquee" aria-hidden="true">
      <div class="hero__marquee-track">
        <span v-for="(t, i) in marqueeText" :key="i">{{ t }}</span>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'HeroSection',
  computed: {
    stats() { return this.$tm('hero.stats') },
    mini() { return this.$tm('hero.mini') },
    trustText() { return this.$tm('hero.trust') },
    // Repeat the list so each half is wider than any viewport, then duplicate
    // it once more — translateX(-50%) lands exactly on the seam for a gapless loop.
    marqueeText() {
      const base = this.trustText
      const half = [...base, ...base, ...base] // 3× → wide enough for ultra-wide screens
      return [...half, ...half]                // duplicate the half for the seamless loop
    }
  }
}
</script>

<style scoped>
.hero { position: relative; padding: 150px 0 90px; overflow: hidden; }

.hero__bg { position: absolute; inset: 0; z-index: 0; }
.hero__grid {
  position: absolute; inset: -2px;
  background-image: linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px);
  background-size: 56px 56px;
  mask-image: radial-gradient(ellipse 80% 60% at 50% 30%, #000 40%, transparent 78%);
  -webkit-mask-image: radial-gradient(ellipse 80% 60% at 50% 30%, #000 40%, transparent 78%);
}
.hero__orb { position: absolute; border-radius: 50%; filter: blur(70px); opacity: .55; }
.hero__orb--1 { width: 520px; height: 520px; background: radial-gradient(circle, #FF9900, transparent 70%); top: -120px; right: -80px; animation: float1 14s ease-in-out infinite; }
.hero__orb--2 { width: 460px; height: 460px; background: radial-gradient(circle, #FFB84D, transparent 70%); top: 120px; left: -140px; animation: float2 17s ease-in-out infinite; }
.hero__orb--3 { width: 420px; height: 420px; background: radial-gradient(circle, #FF6F00, transparent 70%); bottom: -160px; left: 40%; animation: float1 20s ease-in-out infinite; }
.hero__beam {
  position: absolute; top: 0; left: 50%; width: 1px; height: 100%;
  background: linear-gradient(180deg, transparent, rgba(255,153,0,.4), transparent);
}

@keyframes float1 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-26px, 30px); } }
@keyframes float2 { 0%,100% { transform: translate(0,0); } 50% { transform: translate(34px, -24px); } }

.hero__inner {
  position: relative; z-index: 1;
  display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 60px; align-items: center;
}

.hero__badge {
  display: inline-flex; align-items: center; gap: 9px;
  font-size: 13px; font-weight: 600; color: var(--ec-text-soft);
  padding: 8px 16px; border-radius: 999px;
  background: rgba(255,255,255,.05); border: 1px solid var(--ec-line);
}
.hero__badge .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--ec-cyan); box-shadow: 0 0 0 4px rgba(34,211,238,.22); animation: pulse 2s infinite; }
@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: .4; } }

.hero__title { font-size: clamp(40px, 5.4vw, 68px); font-weight: 900; margin: 26px 0 22px; }
.hero__lead { font-size: 18px; color: var(--ec-text-soft); max-width: 540px; }

.hero__actions { display: flex; gap: 14px; margin: 34px 0 44px; flex-wrap: wrap; }

.hero__stats { display: flex; gap: 42px; }
.hero__stat { display: flex; flex-direction: column; }
.hero__stat strong { font-size: 28px; font-weight: 800; background: var(--ec-grad); -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent; }
.hero__stat span { font-size: 13px; color: var(--ec-text-mute); margin-top: 2px; }

/* Visual panel */
.hero__visual { position: relative; }
.panel {
  position: relative; z-index: 2;
  background: linear-gradient(160deg, rgba(20,28,56,.9), rgba(10,15,33,.92));
  border: 1px solid var(--ec-line); border-radius: 22px;
  box-shadow: var(--ec-shadow); backdrop-filter: blur(10px);
  overflow: hidden;
}
.panel__top { display: flex; align-items: center; gap: 12px; padding: 14px 18px; border-bottom: 1px solid var(--ec-line); }
.panel__dots { display: flex; gap: 6px; }
.panel__dots i { width: 11px; height: 11px; border-radius: 50%; background: rgba(255,255,255,.18); }
.panel__dots i:first-child { background: #ff5f57; } .panel__dots i:nth-child(2) { background: #febc2e; } .panel__dots i:nth-child(3) { background: #28c840; }
.panel__title { font-size: 13px; color: var(--ec-text-soft); font-weight: 600; }
.panel__live { margin-left: auto; font-size: 12px; color: #FF9900; display: flex; align-items: center; gap: 6px; }
.panel__live i { width: 7px; height: 7px; border-radius: 50%; background: #FF9900; box-shadow: 0 0 0 4px rgba(255,153,0,.2); }
.panel__body { padding: 22px 20px 26px; }

.metric__row { display: flex; justify-content: space-between; align-items: baseline; font-size: 14px; color: var(--ec-text-soft); margin-bottom: 10px; }
.metric__row strong { font-size: 22px; }
.bar { height: 8px; border-radius: 8px; background: rgba(255,255,255,.07); overflow: hidden; }
.bar span { display: block; height: 100%; background: var(--ec-grad); border-radius: 8px; animation: grow 1.6s cubic-bezier(.16,.84,.44,1) both; }
@keyframes grow { from { width: 0 !important; } }

.metric__grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin: 22px 0; }
.mini { background: rgba(255,255,255,.04); border: 1px solid var(--ec-line); border-radius: 14px; padding: 13px 14px; }
.mini__k { font-size: 12px; color: var(--ec-text-mute); display: block; }
.mini__v { font-size: 22px; display: block; margin: 3px 0; }
.mini__d { font-size: 12px; } .mini__d.up { color: #FF9900; } .mini__d.down { color: #f87171; }

.spark { margin-top: 4px; }
.spark svg { width: 100%; height: 70px; }

.float-card {
  position: absolute; z-index: 3; display: flex; align-items: center; gap: 11px;
  background: rgba(13, 19, 40, 0.92); border: 1px solid var(--ec-line);
  padding: 12px 16px; border-radius: 14px; box-shadow: var(--ec-shadow); backdrop-filter: blur(8px);
}
.float-card__icon { font-size: 20px; }
.float-card strong { display: block; font-size: 14px; }
.float-card em { font-style: normal; font-size: 11.5px; color: var(--ec-text-mute); }
.float-card--a { top: 18px; left: -34px; animation: bob 6s ease-in-out infinite; }
.float-card--b { bottom: 30px; right: -26px; animation: bob 7s ease-in-out infinite reverse; }
@keyframes bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }

/* Marquee */
.hero__marquee { position: relative; z-index: 1; margin-top: 80px; padding: 22px 0; border-top: 1px solid var(--ec-line); border-bottom: 1px solid var(--ec-line); overflow: hidden; -webkit-mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent); mask-image: linear-gradient(90deg, transparent, #000 12%, #000 88%, transparent); }
.hero__marquee-track { display: flex; width: max-content; animation: marquee 26s linear infinite; will-change: transform; }
.hero__marquee-track span { font-size: 16px; font-weight: 600; color: var(--ec-text-mute); white-space: nowrap; letter-spacing: .04em; padding: 0 32px; }
.hero__marquee:hover .hero__marquee-track { animation-play-state: paused; }
@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }

@media (max-width: 980px) {
  .hero__inner { grid-template-columns: 1fr; gap: 56px; }
  .hero__visual { max-width: 480px; }
  .float-card--a { left: 0; } .float-card--b { right: 0; }
}
@media (max-width: 560px) {
  .hero { padding-top: 120px; }
  .hero__stats { gap: 26px; }
  .hero__stat strong { font-size: 23px; }
  .float-card { display: none; }
}
</style>
